import { Link } from "react-router-dom";
import { Heart, MapPin, ArrowUpRight, Sparkles, Gift, HandHeart, TrendingUp, Briefcase, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { getLocalizedOfferTitle, getLocalizedOfferDescription, getOfferSourceLanguageHint } from "@/lib/localizedOffer";
import { trackEvent } from "@/lib/analytics";
import { devWarn } from "@/lib/devLog";

const CATEGORY_BADGE = {
  donation: "bg-[#FBE8CC] text-[#8B5E1F]",
  volunteering: "bg-[#E0EBD3] text-[#3F6147]",
  pro_bono_service: "bg-[#E6E5F2] text-[#4F4880]",
  impact_investment: "bg-[#F2E9D5] text-[#7C642A]",
  fundraising: "bg-[#F5DDD0] text-[#8E4A2E]",
};

// Each category gets its own small lucide icon. Icons are paired with the
// existing text label so categories are not differentiated by color alone
// (WCAG 1.4.1). No new dependencies — all icons already shipped with
// `lucide-react` and used elsewhere in the codebase.
const CATEGORY_ICON = {
  donation: Gift,
  volunteering: HandHeart,
  fundraising: TrendingUp,
  pro_bono_service: Briefcase,
  impact_investment: Coins,
};

export default function OfferCard({ offer }) {
  const { user, favorites, toggleFavorite } = useAuth();
  const { t, i18n } = useTranslation();
  const isFav = favorites.has(offer.id);
  const locale = i18n.language;
  const title = getLocalizedOfferTitle(offer, locale);
  const description = getLocalizedOfferDescription(offer, locale);
  const srcHint = getOfferSourceLanguageHint(offer, locale);

  const onFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      window.location.href = "/login";
      return;
    }
    const willSave = !isFav;
    try {
      await toggleFavorite(offer.id);
    } catch (err) {
      // Favorites is best-effort UI; surface in dev, never block the card.
      devWarn("OfferCard: toggleFavorite failed", err);
    }
    if (willSave) {
      trackEvent("Save Opportunity Click", {
        page_type: "offer_card",
        logged_in: true,
      });
    }
  };

  const country = t(`countries.${offer.country}`, { defaultValue: offer.country });
  const cause = t(`causes.${offer.cause_area}`, { defaultValue: offer.cause_area });
  const catShort = t(`categories.${offer.category}_short`, { defaultValue: offer.category });
  const CatIcon = CATEGORY_ICON[offer.category] || Sparkles;

  return (
    <Link
      to={`/offer/${offer.id}`}
      className="card-clay group relative overflow-hidden flex flex-col"
      data-testid={`offer-card-${offer.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#EFEAE0]">
        {offer.image_url ? (
          <img
            src={offer.image_url}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full eu-map-bg flex flex-col items-center justify-center text-[#3F6147]">
            <Sparkles size={28} strokeWidth={1.4} />
            <span className="mt-2 text-xs font-semibold tracking-wide">{country}</span>
          </div>
        )}
        <button
          onClick={onFav}
          aria-label={isFav ? t("offers.unsave", { defaultValue: "Remove from saved" }) : t("offers.save", { defaultValue: "Save offer" })}
          aria-pressed={isFav}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition ${
            isFav ? "bg-[#5B8C66] text-white" : "bg-white/90 text-[#1F2A24] hover:bg-white"
          }`}
          data-testid={`favorite-btn-${offer.id}`}
        >
          <Heart size={16} strokeWidth={1.8} fill={isFav ? "currentColor" : "none"} />
        </button>
        <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${CATEGORY_BADGE[offer.category] || "bg-white text-[#1F2A24]"}`}>
          <CatIcon size={11} strokeWidth={2} aria-hidden="true" />
          {catShort}
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 text-xs text-[#5F6B61]">
          <MapPin size={12} strokeWidth={1.8} />
          <span>{offer.city ? `${offer.city}, ` : ""}{country}</span>
        </div>
        <h3 className="mt-2 text-lg font-bold leading-snug text-[#1F2A24] line-clamp-2 group-hover:text-[#3F6147] transition-colors">
          {title}
        </h3>
        <p className="mt-1 text-xs text-[#5F6B61] font-medium">{offer.organization}</p>
        <p className="mt-3 text-sm text-[#5F6B61] line-clamp-2 flex-1">{description}</p>

        {srcHint && (
          <p
            className="mt-2 text-[10px] uppercase tracking-wider text-[#8A9388]"
            data-testid={`offer-card-source-lang-${offer.id}`}
            title={t("offers.source_language_hint_long", { defaultValue: "Original text in this language" })}
          >
            {t("offers.source_language_label", { defaultValue: "Original" })}: {srcHint.toUpperCase()}
          </p>
        )}

        <div className="mt-4 pt-4 border-t border-[#DED7C7] flex items-center justify-between">
          <span className="text-xs font-semibold text-[#5B8C66]">{cause}</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#5B8C66]">
            {t("offers.view")} <ArrowUpRight size={12} strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </Link>
  );
}
